import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "../../../../components/Form/TextField";
import Typography from "@mui/material/Typography";
import CardContainer from "../../../../components/Card/CardContainer";
import EditActionButtons from "./EditActionButtons";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  BRAND_SETUP_SECTION_BRAND_DESCRIPTION,
  BRAND_SETUP_SECTION_BRAND_DESCRIPTION_PLACEHOLDER,
  BRAND_SETUP_SECTION_BRAND_NAME,
  BRAND_SETUP_SECTION_BRAND_NAME_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { BRAND_EDIT_MULTILINE_ROWS } from "../utils/constants";

export const BrandSection = ({ formMethods, hasError = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");

  const { t } = useTranslationApp();

  const businessName = formMethods?.watch?.("businessName") || "";
  const businessDescription = formMethods?.watch?.("businessDescription") || "";

  // Sincronizar estados cuando cambien los valores del formulario
  useEffect(() => {
    if (!isEditing && formMethods?.control) {
      setEditedName(businessName);
      setEditedDescription(businessDescription);
    }
  }, [businessName, businessDescription, isEditing, formMethods?.control]);

  if (!formMethods || !formMethods.control) {
    return null;
  }

  const { control, errors, validationRules, trigger } = formMethods;

  const onEditBrandSection = async () => {
    setIsEditing(true);
    // Trigger validation when entering edit mode to show existing errors
    await trigger(["businessName", "businessDescription"]);
  };

  const onSaveBrandSection = () => {
    if (!formMethods?.setValue) return;
    formMethods.setValue("businessName", editedName);
    formMethods.setValue("businessDescription", editedDescription);
    setIsEditing(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Brand Name and Description Card */}
      <CardContainer
        title=""
        sxContainer={{ position: "relative" }}
        hasError={hasError}
      >
        {/* Edit Button in Top-Right Corner */}
        <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 1 }}>
          <EditActionButtons
            isEditing={isEditing}
            onEdit={onEditBrandSection}
            onSave={onSaveBrandSection}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Brand Name */}
          <Box sx={{ display: isEditing ? "block" : "none" }}>
            <TextField
              control={control}
              name="businessName"
              placeholder={t(BRAND_SETUP_SECTION_BRAND_NAME_PLACEHOLDER)}
              value={editedName}
              actionsChange={(value) => setEditedName(value)}
              rules={validationRules.businessName}
              errors={errors}
              autoFocus={isEditing}
              variant="standard"
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  fontSize: "1.5rem",
                  fontWeight: 600,
                },
              }}
            />
          </Box>
          {!isEditing && (
            <Typography
              variant="h4"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color: businessName ? "text.primary" : "fields.placeholder",
              }}
            >
              {businessName || t(BRAND_SETUP_SECTION_BRAND_NAME)}
            </Typography>
          )}

          {/* Brand Description */}
          <Box sx={{ display: isEditing ? "block" : "none" }}>
            <TextField
              control={control}
              name="businessDescription"
              placeholder={t(BRAND_SETUP_SECTION_BRAND_DESCRIPTION_PLACEHOLDER)}
              value={editedDescription}
              actionsChange={(value) => setEditedDescription(value)}
              rules={validationRules.businessDescription}
              errors={errors}
              variant="filled"
              multiline
              minRows={BRAND_EDIT_MULTILINE_ROWS}
              maxRows={BRAND_EDIT_MULTILINE_ROWS}
              sx={{
                width: "100%",
              }}
            />
          </Box>
          {!isEditing && (
            <Typography
              variant="body1"
              sx={{
                color: businessDescription
                  ? "text.secondary"
                  : "fields.placeholder",
                lineHeight: 1.6,
              }}
            >
              {businessDescription || t(BRAND_SETUP_SECTION_BRAND_DESCRIPTION)}
            </Typography>
          )}
        </Box>
      </CardContainer>
    </Box>
  );
};

export default BrandSection;
